import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import * as React from 'react';
import { AuthContext, AuthData } from '../../context/auth';
import { ClaimContext, ClaimData } from '../../context/claim';
import { PolicyContext, PolicyData } from '../../context/policy';
import { LedgerLineItem } from '../../types/claim';
import { CurrencyCode } from '../../types/currencyCode';
import { formatCurrency, formatUNIXTimestampWithTime } from '../../util/util';
import { StyledTitle } from '../common';
import PayableDetailsModal from './PayableDetailsModal';
import { formatLedgerType } from './util';

interface Props {
	authData: AuthData;
	policyData: PolicyData;
	claimData: ClaimData;
	ledgerEntries: LedgerLineItem[];
	ledgerTitle: string;
	currency: CurrencyCode;
}

const SubclaimLedger = (props: Props) => {
	const { ledgerEntries, ledgerTitle, currency, authData } = props;

	const renderTableHeaders = () => {
		return (
			<TableHead>
				<TableRow>
					<TableCell>Date</TableCell>
					<TableCell>Update Type</TableCell>
					<TableCell align='right'>Amount</TableCell>
					<TableCell align='right'>Available Reserve</TableCell>
				</TableRow>
			</TableHead>
		);
	};

	const renderLedgerRow = (ledger: LedgerLineItem, index: number) => {
		if (ledger.payableLocator) {
			return (
				<PayableDetailsModal
					locator={ledger.payableLocator}
					ledger={ledger}
					currency={currency}
				/>
			);
		} else {
			const availableReserve = formatCurrency(
				Math.abs(ledger.reserveBalance),
				currency,
			);
			return (
				<TableRow key={`${ledger.changeTimestamp}-${index}`}>
					<TableCell scope='row'>
						{formatUNIXTimestampWithTime(
							ledger.changeTimestamp,
							authData.auth.timezone,
						)}
					</TableCell>
					<TableCell>{formatLedgerType(ledger.changeType)}</TableCell>
					<TableCell align='right' />
					<TableCell align='right'>{availableReserve}</TableCell>
				</TableRow>
			);
		}
	};

	return (
		<div style={{ marginBottom: '20px' }}>
			<StyledTitle>{ledgerTitle}</StyledTitle>
			<TableContainer>
				<Table size='small'>
					{renderTableHeaders()}
					<TableBody>
						{ledgerEntries.map((ledger, index) =>
							renderLedgerRow(ledger, index),
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</div>
	);
};

function withContext(Component: any) {
	return (props: Omit<Props, 'authData' | 'claimData' | 'policyData'>) => (
		<AuthContext.Consumer>
			{(authData) => (
				<PolicyContext.Consumer>
					{(policyData) => (
						<ClaimContext.Consumer>
							{(claimData) => (
								<Component
									{...props}
									authData={authData}
									policyData={policyData}
									claimData={claimData}
								/>
							)}
						</ClaimContext.Consumer>
					)}
				</PolicyContext.Consumer>
			)}
		</AuthContext.Consumer>
	);
}

export default withContext(SubclaimLedger);
